import { makeAutoObservable, runInAction } from 'mobx';
import DashboardTranslationLocaleStore from './dashboard-translations-locale-store';
import DashboardTranslationsService, { IDashboardTranslationLocale } from '../../services/api/dashboard-translations-service';
import { TYPE, rootContainer } from '../../services/container/root';


interface ILocaleMap {
    [locale: string]: DashboardTranslationLocaleStore
}

const DASHBOARD_TRANSLATIONS_CACHE_KEY = 'dashboard-translations-cache';

class DashboardTranslationStore {
    /** private fields */
    private _isLoading: boolean = false;
    private _dashboard_locale: string = '';
    private _locales: {[key :string]: string} = {};
    private localeMap: ILocaleMap = {};

	constructor() {
		makeAutoObservable(this, {}, { autoBind: true });
	}

    private get translationsService(): DashboardTranslationsService {
		return rootContainer.get<DashboardTranslationsService>(TYPE.DashboardTranslationsService);
	}  

    /** public getters */
    public get isLoading() { return this._isLoading; }
    public get locale() { return this._dashboard_locale; }
    public get locales() { return this._locales; }

    public async load() {
        this.callAndSetModels(() => this.translationsService.getDashboardTranslations());
    }

    public async setLocale(locale: string) {
        this.callAndSetModels(() => this.translationsService.setDashboardTranslations(locale));
    }

    private async callAndSetModels(getTransMethod: () => Promise<IDashboardTranslationLocale>): Promise<void> {
        const cachedResponse = this.getCachedResponse();
        if (cachedResponse) {
            this.setupModels(cachedResponse);
        }

        try {
            this._isLoading = true;
            const dashboardTransResult = await getTransMethod();
            this.saveResponseCache(dashboardTransResult);
            runInAction(() => {
                this.setupModels(dashboardTransResult);
                this._isLoading = false;
            });
        }
        catch (e) {
            runInAction(() => {
                this._isLoading = false;
            });
            console.error(e);
        }
    }

    private setupModels(response: IDashboardTranslationLocale): void {
        this._dashboard_locale = response.dashboard_locale;
        this._locales = response.locales;
        this.localeMap[response.dashboard_locale] = new DashboardTranslationLocaleStore(response.translations);
    }

    private getCachedResponse(): IDashboardTranslationLocale | undefined {
        if (typeof window === 'undefined' || !window.localStorage) {
            console.error('DashboardTranslationStore.getCachedResponse: window not initiated or localStorage is not available');
            return undefined;
        }

        try {
            const cached = window.localStorage.getItem(DASHBOARD_TRANSLATIONS_CACHE_KEY);
            if (!cached) {
                return undefined;
            }

            const parsed = JSON.parse(cached) as IDashboardTranslationLocale | null;

            // basic shape validation to avoid runtime issues with bad cache
            if (
                !parsed ||
                typeof parsed.dashboard_locale !== 'string' ||
                typeof parsed.locales !== 'object' ||
                !parsed.translations
            ) {
                return undefined;
            }

            return parsed;
        } catch (e) {
            console.error('Failed to read dashboard translations cache', e);
            return undefined;
        }
    }

    private saveResponseCache(data: IDashboardTranslationLocale): void {
        if (typeof window === 'undefined' || !window.localStorage) {
            console.error('DashboardTranslationStore.saveResponseCache: window not initiated or localStorage is not available');
            return;
        }

        try {
            const serialized = JSON.stringify(data);
            window.localStorage.setItem(DASHBOARD_TRANSLATIONS_CACHE_KEY, serialized);
        } catch (e) {
            console.error('Failed to save dashboard translations cache', e);
        }
    }

    /** if translations were not yet loaded - returns empty string */
    public getTranslation(key: string, scope: string, params: string[] = []): string {
        return this.localeMap[this._dashboard_locale]?.getTranslation(key, scope, params) ?? '';
    }
    
}

export default new DashboardTranslationStore();